import React, { useState, useEffect } from "react";
import axios from "axios";
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from "chart.js";
import { Pie, Bar } from "react-chartjs-2";
import './Financials.css';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const Financials = () => {
  const [statementType, setStatementType] = useState("balance_sheet");
  const [statementData, setStatementData] = useState(null);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";

  // MOCK DATA FOR UI PREVIEW - Remove this block for production
  const MOCK_DATA = {
    balance_sheet: {
      statement_type: "balance_sheet",
      data: {
        assets: { cash_and_equivalents: 125000.50, investments: 875000.00, total_assets: 1000000.50 },
        liabilities: { margin_balance: 15000.00, accrued_fees: 2500.00, total_liabilities: 17500.00 },
        equity: { net_asset_value: 982500.50, total_equity: 982500.50 },
        date: "2026-01-23"
      }
    },
    income_statement: {
      statement_type: "income_statement",
      data: {
        revenue: { investment_gains_losses: 45000.00, dividend_income: 8500.00, interest_income: 1200.00, total_revenue: 54700.00 },
        expenses: { management_fee: 2500.00, carried_interest: 4500.00, transaction_fees: 350.00, total_expenses: 7350.00 },
        net_income: 47350.00,
        start_date: "2025-01-01",
        end_date: "2026-01-23"
      }
    },
    cash_flow_statement: {
      statement_type: "cash_flow_statement",
      data: {
        operating_activities: { dividend_income: 8500.00, interest_income: 1200.00, fees_paid: -350.00, net_operating_cash_flow: 9350.00 },
        investing_activities: { purchases: -150000.00, sales: 95000.00, net_investing_cash_flow: -55000.00 },
        financing_activities: { capital_contributions: 50000.00, withdrawals: -10000.00, net_financing_cash_flow: 40000.00 },
        net_cash_flow: -5650.00,
        cash_beginning: 130650.50,
        cash_ending: 125000.50,
        start_date: "2025-01-01",
        end_date: "2026-01-23"
      }
    }
  };
  const USE_MOCK_DATA = false; // Set to false for production
  // END MOCK DATA

  const fetchStatement = async () => {
    setLoading(true);
    setError("");

    // Use mock data for UI preview
    if (USE_MOCK_DATA) {
      setTimeout(() => {
        setStatementData(MOCK_DATA[statementType]);
        setLoading(false);
      }, 500); // Simulate loading
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE_URL}/get-financial-statement`,
        { statement_type: statementType, start_date: startDate, end_date: endDate },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("authToken")}` } }
      );
      setStatementData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch financial statement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatement();
  }, [statementType, startDate, endDate]);

  const handleDownloadPDF = async () => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}/download-financial-statement`,
        { statement_type: statementType, start_date: startDate, end_date: endDate },
        {
          headers: { Authorization: `Bearer ${sessionStorage.getItem("authToken")}` },
          responseType: "blob",
        }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${statementType}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError("Failed to download PDF: " + (err.response?.data?.detail || "Unknown error"));
    }
  };

  const formatCurrency = (value) => {
    const num = Number(value) || 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Chart options
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: '#e0e0e0',
          font: { size: 12 }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return ` ${context.label}: ${formatCurrency(value)}`;
          }
        }
      }
    }
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: (context) => formatCurrency(context.raw)
        }
      }
    },
    scales: {
      x: {
        ticks: { color: '#e0e0e0' },
        grid: { color: 'rgba(255,255,255,0.1)' }
      },
      y: {
        ticks: {
          color: '#e0e0e0',
          callback: (value) => formatCurrency(value)
        },
        grid: { color: 'rgba(255,255,255,0.1)' }
      }
    }
  };

  const renderBalanceSheet = (data) => {
    const assetChartData = {
      labels: ['Cash & Equivalents', 'Investments'],
      datasets: [{
        data: [data.assets.cash_and_equivalents, data.assets.investments],
        backgroundColor: ['#4ecdc4', '#ff6b6b'],
        borderColor: ['#3db8b0', '#e85555'],
        borderWidth: 2
      }]
    };

    return (
      <div className="statement-content">
        <div className="statement-header">
          <h2>Balance Sheet</h2>
          <span className="statement-date">As of {data.date}</span>
        </div>

        <div className="statement-grid">
          {/* Assets Card */}
          <div className="glass-card">
            <h3>Assets</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Cash and Equivalents</span>
                <span className="amount">{formatCurrency(data.assets.cash_and_equivalents)}</span>
              </div>
              <div className="line-item">
                <span>Investments</span>
                <span className="amount">{formatCurrency(data.assets.investments)}</span>
              </div>
              <div className="line-item total">
                <span>Total Assets</span>
                <span className="amount">{formatCurrency(data.assets.total_assets)}</span>
              </div>
            </div>
          </div>

          {/* Liabilities Card */}
          <div className="glass-card">
            <h3>Liabilities</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Margin Balance</span>
                <span className="amount">{formatCurrency(data.liabilities.margin_balance)}</span>
              </div>
              <div className="line-item">
                <span>Accrued Fees</span>
                <span className="amount">{formatCurrency(data.liabilities.accrued_fees)}</span>
              </div>
              <div className="line-item total">
                <span>Total Liabilities</span>
                <span className="amount">{formatCurrency(data.liabilities.total_liabilities)}</span>
              </div>
            </div>
          </div>

          {/* Equity Card */}
          <div className="glass-card">
            <h3>Equity</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Net Asset Value</span>
                <span className="amount">{formatCurrency(data.equity.net_asset_value)}</span>
              </div>
              <div className="line-item total">
                <span>Total Equity</span>
                <span className="amount">{formatCurrency(data.equity.total_equity)}</span>
              </div>
            </div>
          </div>

          {/* Asset Allocation Chart */}
          <div className="glass-card chart-card">
            <h3>Asset Allocation</h3>
            <div className="chart-wrapper">
              <Pie data={assetChartData} options={pieOptions} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderIncomeStatement = (data) => {
    const revenueExpenseChartData = {
      labels: ['Investment Gains', 'Dividends', 'Interest', 'Mgmt Fee', 'Carried Int.', 'Tx Fees'],
      datasets: [{
        label: 'Amount',
        data: [
          data.revenue.investment_gains_losses,
          data.revenue.dividend_income,
          data.revenue.interest_income,
          -data.expenses.management_fee,
          -data.expenses.carried_interest,
          -data.expenses.transaction_fees
        ],
        backgroundColor: [
          data.revenue.investment_gains_losses >= 0 ? '#4ecdc4' : '#ff6b6b',
          '#4ecdc4',
          '#4ecdc4',
          '#ff6b6b',
          '#ff6b6b',
          '#ff6b6b'
        ],
        borderColor: [
          data.revenue.investment_gains_losses >= 0 ? '#3db8b0' : '#e85555',
          '#3db8b0',
          '#3db8b0',
          '#e85555',
          '#e85555',
          '#e85555'
        ],
        borderWidth: 2
      }]
    };

    return (
      <div className="statement-content">
        <div className="statement-header">
          <h2>Income Statement</h2>
          <span className="statement-date">{data.start_date} to {data.end_date}</span>
        </div>

        <div className="statement-grid">
          {/* Revenue Card */}
          <div className="glass-card">
            <h3>Revenue</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Investment Gains/Losses</span>
                <span className={`amount ${data.revenue.investment_gains_losses >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(data.revenue.investment_gains_losses)}
                </span>
              </div>
              <div className="line-item">
                <span>Dividend Income</span>
                <span className="amount positive">{formatCurrency(data.revenue.dividend_income)}</span>
              </div>
              <div className="line-item">
                <span>Interest Income</span>
                <span className="amount positive">{formatCurrency(data.revenue.interest_income)}</span>
              </div>
              <div className="line-item total">
                <span>Total Revenue</span>
                <span className={`amount ${data.revenue.total_revenue >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(data.revenue.total_revenue)}
                </span>
              </div>
            </div>
          </div>

          {/* Expenses Card */}
          <div className="glass-card">
            <h3>Expenses</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Management Fee</span>
                <span className="amount negative">{formatCurrency(data.expenses.management_fee)}</span>
              </div>
              <div className="line-item">
                <span>Carried Interest</span>
                <span className="amount negative">{formatCurrency(data.expenses.carried_interest)}</span>
              </div>
              <div className="line-item">
                <span>Transaction Fees</span>
                <span className="amount negative">{formatCurrency(data.expenses.transaction_fees)}</span>
              </div>
              <div className="line-item total">
                <span>Total Expenses</span>
                <span className="amount negative">{formatCurrency(data.expenses.total_expenses)}</span>
              </div>
            </div>
          </div>

          {/* Net Income Card */}
          <div className="glass-card highlight-card">
            <h3>Net Income</h3>
            <div className="card-content">
              <div className="big-number">
                <span className={data.net_income >= 0 ? 'positive' : 'negative'}>
                  {formatCurrency(data.net_income)}
                </span>
              </div>
            </div>
          </div>

          {/* Revenue vs Expenses Chart */}
          <div className="glass-card chart-card wide">
            <h3>Revenue vs Expenses</h3>
            <div className="chart-wrapper bar-chart">
              <Bar data={revenueExpenseChartData} options={barOptions} />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCashFlowStatement = (data) => {
    if (!data.operating_activities || !data.investing_activities || !data.financing_activities) {
      return <div className="glass-card"><p>No valid data available for the cash flow statement.</p></div>;
    }

    const cashFlowChartData = {
      labels: ['Operating', 'Investing', 'Financing'],
      datasets: [{
        label: 'Cash Flow',
        data: [
          data.operating_activities.net_operating_cash_flow,
          data.investing_activities.net_investing_cash_flow,
          data.financing_activities.net_financing_cash_flow
        ],
        backgroundColor: [
          data.operating_activities.net_operating_cash_flow >= 0 ? '#4ecdc4' : '#ff6b6b',
          data.investing_activities.net_investing_cash_flow >= 0 ? '#4ecdc4' : '#ff6b6b',
          data.financing_activities.net_financing_cash_flow >= 0 ? '#4ecdc4' : '#ff6b6b'
        ],
        borderColor: [
          data.operating_activities.net_operating_cash_flow >= 0 ? '#3db8b0' : '#e85555',
          data.investing_activities.net_investing_cash_flow >= 0 ? '#3db8b0' : '#e85555',
          data.financing_activities.net_financing_cash_flow >= 0 ? '#3db8b0' : '#e85555'
        ],
        borderWidth: 2
      }]
    };

    return (
      <div className="statement-content">
        <div className="statement-header">
          <h2>Cash Flow Statement</h2>
          <span className="statement-date">{data.start_date} to {data.end_date}</span>
        </div>

        <div className="statement-grid">
          {/* Operating Activities Card */}
          <div className="glass-card">
            <h3>Operating Activities</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Dividend Income</span>
                <span className="amount">{formatCurrency(data.operating_activities.dividend_income)}</span>
              </div>
              <div className="line-item">
                <span>Interest Income</span>
                <span className="amount">{formatCurrency(data.operating_activities.interest_income)}</span>
              </div>
              <div className="line-item">
                <span>Fees Paid</span>
                <span className="amount negative">{formatCurrency(data.operating_activities.fees_paid)}</span>
              </div>
              <div className="line-item total">
                <span>Net Operating Cash Flow</span>
                <span className={`amount ${data.operating_activities.net_operating_cash_flow >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(data.operating_activities.net_operating_cash_flow)}
                </span>
              </div>
            </div>
          </div>

          {/* Investing Activities Card */}
          <div className="glass-card">
            <h3>Investing Activities</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Purchases</span>
                <span className="amount negative">{formatCurrency(data.investing_activities.purchases)}</span>
              </div>
              <div className="line-item">
                <span>Sales</span>
                <span className="amount positive">{formatCurrency(data.investing_activities.sales)}</span>
              </div>
              <div className="line-item total">
                <span>Net Investing Cash Flow</span>
                <span className={`amount ${data.investing_activities.net_investing_cash_flow >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(data.investing_activities.net_investing_cash_flow)}
                </span>
              </div>
            </div>
          </div>

          {/* Financing Activities Card */}
          <div className="glass-card">
            <h3>Financing Activities</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Capital Contributions</span>
                <span className="amount positive">{formatCurrency(data.financing_activities.capital_contributions)}</span>
              </div>
              <div className="line-item">
                <span>Withdrawals</span>
                <span className="amount negative">{formatCurrency(data.financing_activities.withdrawals)}</span>
              </div>
              <div className="line-item total">
                <span>Net Financing Cash Flow</span>
                <span className={`amount ${data.financing_activities.net_financing_cash_flow >= 0 ? 'positive' : 'negative'}`}>
                  {formatCurrency(data.financing_activities.net_financing_cash_flow)}
                </span>
              </div>
            </div>
          </div>

          {/* Cash Flow Chart */}
          <div className="glass-card chart-card">
            <h3>Cash Flow by Activity</h3>
            <div className="chart-wrapper">
              <Bar data={cashFlowChartData} options={barOptions} />
            </div>
          </div>

          {/* Net Cash Flow Card */}
          <div className="glass-card highlight-card">
            <h3>Net Cash Flow</h3>
            <div className="card-content">
              <div className="big-number">
                <span className={data.net_cash_flow >= 0 ? 'positive' : 'negative'}>
                  {formatCurrency(data.net_cash_flow)}
                </span>
              </div>
            </div>
          </div>

          {/* Cash Balance Card */}
          <div className="glass-card">
            <h3>Cash Balance</h3>
            <div className="card-content">
              <div className="line-item">
                <span>Beginning Cash</span>
                <span className="amount">{formatCurrency(data.cash_beginning)}</span>
              </div>
              <div className="line-item total">
                <span>Ending Cash</span>
                <span className="amount">{formatCurrency(data.cash_ending)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="financials-container">
      <h1>Financial Statements</h1>

      {/* Controls */}
      <div className="controls-bar">
        <div className="control-group">
          <label>Statement Type</label>
          <select
            value={statementType}
            onChange={(e) => setStatementType(e.target.value)}
            className="glass-select"
          >
            <option value="balance_sheet">Balance Sheet</option>
            <option value="income_statement">Income Statement</option>
            <option value="cash_flow_statement">Cash Flow Statement</option>
          </select>
        </div>

        <div className="control-group">
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="glass-input"
          />
        </div>

        <div className="control-group">
          <label>End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="glass-input"
          />
        </div>

        <button
          onClick={handleDownloadPDF}
          disabled={loading || !statementData}
          className="download-btn"
        >
          Download PDF
        </button>
      </div>

      {/* Status Indicators */}
      {loading && (
        <div className="status-badge loading">
          <span className="spinner"></span>
          Loading...
        </div>
      )}

      {error && (
        <div className="status-badge error">
          {error}
        </div>
      )}

      {!loading && !error && statementData && (
        <div className="status-badge success">
          Data up to date
        </div>
      )}

      {/* Statement Content */}
      {statementData && !loading && (
        <>
          {statementData.statement_type === "balance_sheet" && renderBalanceSheet(statementData.data)}
          {statementData.statement_type === "income_statement" && renderIncomeStatement(statementData.data)}
          {statementData.statement_type === "cash_flow_statement" && renderCashFlowStatement(statementData.data)}
        </>
      )}
    </div>
  );
};

export default Financials;
